import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { FaUpload, FaSpinner, FaCheckCircle, FaTimesCircle, FaCamera } from 'react-icons/fa';

const CertificateVerification = () => {
  const { t } = useTranslation();
  const [certId, setCertId] = useState('');
  const [verificationResult, setVerificationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const videoRef = useRef(null);
  
  const handleVerify = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`/api/verify-certificate/${certId}`);
      setVerificationResult(response.data);
      setIsCapturing(false);
    } catch (err) {
      setError(err.response?.data?.detail || t('certificate_not_found'));
    } finally {
      setLoading(false);
    }
  };

  const startCamera = async () => {
    try {
      setIsCapturing(true);
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      videoRef.current.srcObject = stream;
      videoRef.current.play();
    } catch (err) {
      console.error('Error accessing camera:', err);
      setError(t('camera_error'));
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    setIsCapturing(false);
  };

  const captureImage = () => {
    // Create a canvas element to capture the video frame
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    canvas.getContext('2d').drawImage(videoRef.current, 0, 0);

    // Convert the canvas to a blob and create a file
    canvas.toBlob(async (blob) => {
      const file = new File([blob], "qr-capture.png", { type: "image/png" });
      await handleQRImage(file);
    }, 'image/png');

    stopCamera();
  };

  const handleQRImage = async (file) => {
    const formData = new FormData();
    formData.append('qr_image', file);

    try {
      const response = await axios.post('/api/decode-qr', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data && response.data.cert_id) {
        setCertId(response.data.cert_id);
        handleVerify();
      } else {
        setError(t('invalid_qr_code'));
      }
    } catch (err) {
      console.error('Error processing QR code:', err);
      setError(t('qr_processing_error'));
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <h2 className="text-2xl font-bold text-center text-gray-900 mb-2">
        {t('certificate_verification_title')}
      </h2>
      <p className="text-center text-gray-600 mb-6">
        {t('certificate_verification_description')}
      </p>
      
      <div className="text-center mb-6">
        <button
          onClick={isCapturing ? stopCamera : startCamera}
          className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition flex items-center gap-2 mx-auto"
        >
          <FaCamera />
          {isCapturing ? t('stop_camera') : t('start_camera')}
        </button>
      </div>

      {isCapturing && (
        <div className="mb-6">
          <div className="relative">
            <video
              ref={videoRef}
              style={{ width: '100%', maxHeight: '300px', objectFit: 'contain' }}
              playsInline
            />
            <button
              onClick={captureImage}
              className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-white py-2 px-4 rounded-lg shadow-lg hover:bg-gray-100 transition"
            >
              {t('capture_photo')}
            </button>
          </div>
        </div>
      )}
      
      <form onSubmit={handleVerify} className="mb-8">
        <p className="text-center text-gray-600 mb-4">
          {t('scan_qr_prompt')}
        </p>
        <div className="mb-4">
          <label htmlFor="certId" className="block text-gray-700 mb-2">
            {t('certificate_id')}
          </label>
          <input
            type="text"
            id="certId"
            value={certId}
            onChange={(e) => setCertId(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder={t('enter_certificate_id')}
            required
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          className={`w-full py-2 px-4 rounded-lg font-bold text-white flex items-center justify-center gap-2 ${
            loading ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'
          }`}
        >
          {loading ? (
            <>
              <FaSpinner className="animate-spin" />
              {t('verifying')}
            </>
          ) : (
            t('verify_certificate_button')
          )}
        </button>
      </form>

      {error && (
        <div className="p-4 mb-4 bg-red-100 rounded-lg border-l-4 border-red-500 text-red-700 flex items-center gap-2">
          <FaTimesCircle />
          {error}
        </div>
      )}

      {verificationResult && (
        <div className={`p-6 rounded-lg ${
          verificationResult.is_valid 
            ? 'bg-green-50 border-green-500' 
            : 'bg-red-50 border-red-500'
        } border-2`}>
          <div className="flex items-center gap-2 mb-4">
            {verificationResult.is_valid ? (
              <FaCheckCircle className="text-2xl text-green-500" />
            ) : (
              <FaTimesCircle className="text-2xl text-red-500" />
            )}
            <h3 className="text-xl font-bold">
              {verificationResult.is_valid ? t('certificate_valid') : t('certificate_invalid')}
            </h3>
          </div>
          
          {verificationResult.is_valid && verificationResult.watch_details && (
            <div className="space-y-2 mt-4">
              <p><strong>{t('watch_model')}:</strong> {verificationResult.watch_details.model}</p>
              <p><strong>{t('authenticity')}:</strong> {
                verificationResult.watch_details.is_authentic ? t('genuine') : t('not_genuine')
              }</p>
              <p><strong>{t('confidence_score')}:</strong> {verificationResult.watch_details.confidence_score}%</p>
              <p><strong>{t('issue_date')}:</strong> {new Date(verificationResult.issued_date).toLocaleString()}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CertificateVerification;
